import { supabase } from '@/integrations/supabase/client';

type LinkInput = {
  label?: string;
  url?: string;
  description?: string;
};

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const isValidUuid = (value: string | undefined | null): value is string => {
  return typeof value === 'string' && UUID_REGEX.test(value);
};

export const getOrCreateSidebarId = async (roadmapId: string): Promise<string> => {
  const { data: sidebarData, error: fetchError } = await supabase
    .from('sidebars')
    .select('id')
    .eq('roadmap_id', roadmapId)
    .maybeSingle();

  if (fetchError) {
    throw fetchError;
  }

  if (sidebarData?.id) {
    return sidebarData.id;
  }

  const { data: inserted, error: insertError } = await supabase
    .from('sidebars')
    .insert({ roadmap_id: roadmapId })
    .select('id')
    .single();

  if (insertError || !inserted?.id) {
    throw insertError;
  }

  return inserted.id;
};

interface SyncFieldSidebarToolsParams {
  roadmapId: string;
  stepId?: string | null;
  fieldId: string;
  links: LinkInput[];
}

export const syncFieldSidebarTools = async ({
  roadmapId,
  stepId,
  fieldId,
  links,
}: SyncFieldSidebarToolsParams): Promise<void> => {
  if (!isValidUuid(fieldId)) {
    return;
  }

  const sidebarId = await getOrCreateSidebarId(roadmapId);

  const { error: deleteError } = await supabase
    .from('sidebar_tools')
    .delete()
    .eq('prompt_field_id', fieldId);

  if (deleteError) {
    throw deleteError;
  }

  if (!links || links.length === 0) {
    return;
  }

  const toolsToInsert = links.map((link, index) => ({
    sidebar_id: sidebarId,
    prompt_field_id: fieldId,
    step_id: isValidUuid(stepId) ? stepId : null,
    label: link.label || '',
    url: link.url || '',
    description: link.description || null,
    position: index + 1,
  }));

  const { error: insertError } = await supabase
    .from('sidebar_tools')
    .insert(toolsToInsert)
    .select();

  if (insertError) {
    throw insertError;
  }
};
